import { useEffect } from 'react';
import { BRAND, pageSeoOf } from '@tarot/shared';

/**
 * 고정 경로의 메타데이터를 shared 단일 출처(seo.ts)에서 가져와 적용한다.
 * 빌드 시 만드는 정적 HTML과 같은 값을 써야 크롤러가 본 문서와 어긋나지 않는다.
 */
export function useStaticPageMeta(path: string) {
  const seo = pageSeoOf(path);
  useDocumentMeta({
    title: seo?.title ?? BRAND.title,
    description: seo?.description ?? BRAND.description,
    canonical: `${BRAND.siteUrl}${path}`,
  });
}

/**
 * 페이지별 <title>·meta·canonical 갱신 (SPA용).
 * 크롤러 대응의 본체는 빌드 시 프리렌더/Pages Function이지만,
 * 사용자가 앱 안에서 이동할 때도 탭 제목과 공유 정보가 맞아야 한다.
 */
export function useDocumentMeta(m: { title: string; description?: string; canonical?: string }) {
  useEffect(() => {
    const prev = document.title;
    document.title = m.title;

    const set = (selector: string, create: () => HTMLElement, value: string) => {
      let el = document.head.querySelector<HTMLElement>(selector);
      if (!el) {
        el = create();
        document.head.appendChild(el);
      }
      if (el.tagName === 'LINK') el.setAttribute('href', value);
      else el.setAttribute('content', value);
    };

    if (m.description) {
      set(
        'meta[name="description"]',
        () => {
          const e = document.createElement('meta');
          e.setAttribute('name', 'description');
          return e;
        },
        m.description,
      );
      set(
        'meta[property="og:description"]',
        () => {
          const e = document.createElement('meta');
          e.setAttribute('property', 'og:description');
          return e;
        },
        m.description,
      );
    }
    set(
      'meta[property="og:title"]',
      () => {
        const e = document.createElement('meta');
        e.setAttribute('property', 'og:title');
        return e;
      },
      m.title,
    );
    if (m.canonical) {
      set(
        'link[rel="canonical"]',
        () => {
          const e = document.createElement('link');
          e.setAttribute('rel', 'canonical');
          return e;
        },
        m.canonical,
      );
      set(
        'meta[property="og:url"]',
        () => {
          const e = document.createElement('meta');
          e.setAttribute('property', 'og:url');
          return e;
        },
        m.canonical,
      );
    }

    return () => {
      document.title = prev;
    };
  }, [m.title, m.description, m.canonical]);
}
